export type VideoPlatform = 'facebook' | 'youtube'

function iframeSrc(value: string) {
  const text = value.trim()
  if (!text.startsWith('<iframe')) return text
  return text.match(/src=["']([^"']+)["']/i)?.[1] ?? ''
}

function parseUrl(value: string) {
  const text = iframeSrc(value)
  if (!text) return null
  try {
    return new URL(text)
  } catch {
    return null
  }
}

function hostMatches(url: URL, value: string) {
  const host = url.hostname.toLowerCase().replace(/^www\./, '')
  return host === value || host.endsWith(`.${value}`)
}

export function getYouTubeId(value: string) {
  const url = parseUrl(value)
  if (!url) return ''
  const parts = url.pathname.split('/').filter(Boolean)
  if (hostMatches(url, 'youtu.be')) return parts[0] ?? ''
  if (!hostMatches(url, 'youtube.com')) return ''
  if (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live') return parts[1] ?? ''
  return url.searchParams.get('v') ?? ''
}

export function getYouTubeEmbedUrl(value: string) {
  const id = getYouTubeId(value)
  return id ? `https://www.youtube.com/embed/${id}` : ''
}

export function getFacebookSourceUrl(value: string): string {
  const url = parseUrl(value)
  if (!url) return ''
  if (!hostMatches(url, 'facebook.com') && !hostMatches(url, 'fb.watch')) return ''
  if (url.pathname.startsWith('/plugins/video.php')) {
    const href = url.searchParams.get('href')
    return href ? getFacebookSourceUrl(href) : ''
  }
  url.hash = ''
  return url.toString()
}

function isFacebookShareUrl(value: string) {
  const url = parseUrl(value)
  return !!url && hostMatches(url, 'facebook.com') && url.pathname.startsWith('/share/')
}

export function getFacebookEmbedUrl(value: string) {
  const sourceUrl = getFacebookSourceUrl(value)
  if (!sourceUrl) return ''
  if (isFacebookShareUrl(sourceUrl)) return ''
  return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(sourceUrl)}&show_text=false&width=420`
}

export function getVideoEmbedUrl(platform: VideoPlatform, value: string) {
  return platform === 'facebook' ? getFacebookEmbedUrl(value) : getYouTubeEmbedUrl(value)
}
